'use client'

import SlideToggle from '@/components/ui/SlideToggle'

import { useState, useRef } from 'react'

export default function SdgWizardCheckboxes({
  name,
  choices,
  state,
  setState,
  cols = 2,
}: {
  name: string
  choices: WP_Term_Object[]
  state: any
  setState: (state: any) => void
  cols?: 1 | 2
}) {
  const fieldRef = useRef(null)

  const [keyword, setKeyword] = useState('')
  const [selectedValues, setSelectedValues] = useState(state[name] || [])

  if (!choices || choices.length === 0) {
    return null
  }

  const handleCheckboxChange = () => {
    if (!fieldRef.current) {
      return
    }

    const checkboxes = fieldRef.current.querySelectorAll('input[type="checkbox"]')

    const newSelectedValues = Array.from(checkboxes)
      .filter((checkbox: HTMLInputElement) => checkbox.checked)
      .map((checkbox: HTMLInputElement) => parseInt(checkbox.value))

    setSelectedValues(newSelectedValues)

    state[name] = newSelectedValues

    setState(state)
  }

  const SelectedItems = () => (
    <div className="flex h-full flex-col gap-xs">
      <span className="p--small mb-0">Your selection</span>

      {choices.map((choice) => {
        return (
          <div
            key={choice.term_id}
            className={`${selectedValues.includes(choice.term_id) ? '' : 'hidden'}`}>
            <SelectedItem item={choice} />
          </div>
        )
      })}
    </div>
  )

  const maxHeight = `max-h-[max(75vh,_500px)]`

  return (
    <div className="grid grid-cols-[1fr,24px,1fr] items-start gap-s ms:grid-cols-1">
      <div className={`flex flex-col gap-s ${maxHeight} overflow-hidden`}>
        <input
          type="search"
          onChange={(e) => setKeyword(e.target.value)}
          defaultValue={keyword}
          placeholder="Search"
        />

        <div
          className={`grid max-h-full gap-xs overflow-auto ms:grid-cols-1 ${cols === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}
          ref={fieldRef}>
          {choices.map((choice) => {
            if (!choice || !choice.term_id || !choice.name) {
              return null
            }

            const isChecked = selectedValues.includes(choice.term_id)
            const isVisible = choice.name.toLowerCase().includes(keyword.toLowerCase())

            let btnClass = `
              inline-flex
              min-h-[48px]
              cursor-pointer
              appearance-none
              items-center
              justify-center
              gap-xxs
              rounded-sm
              px-l
              py-xs
              text-center
              align-top
              font-normal
              text-[length:--p-s-size]
              leading-[120%]

              border
              border-fg-title
              text-fg-title
            `

            if (!isVisible) {
              btnClass += ' hidden'
            }

            btnClass += isChecked ? ' bg-fg-title text-white' : ' bg-white text-fg'

            return (
              <label
                key={`checkbox-${choice.term_id}`}
                className={`relative flex items-center px-[30px] ${btnClass}`}>
                <input
                  type="checkbox"
                  className="hidden"
                  id={choice.term_id}
                  defaultChecked={selectedValues.includes(choice.term_id)}
                  value={choice.term_id}
                  onChange={handleCheckboxChange}
                />

                <span>{choice.name}</span>

                {isChecked ? (
                  <svg
                    className="absolute right-[16px] top-1/2 h-[18px] w-[18px] -translate-y-1/2"
                    aria-hidden="true">
                    <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-close`}></use>
                  </svg>
                ) : null}
              </label>
            )
          })}
        </div>
      </div>

      <div className="flex h-full items-center ms:hidden">
        <svg aria-hidden="true" className="h-[24px] w-[24px] shrink-0">
          <use href={`${process.env.NEXT_PUBLIC_SPRITE_PATH}#icon-arrow-right`}></use>
        </svg>
      </div>

      <div
        className={`${selectedValues.length < 1 ? 'items-center' : 'items-start'} flex h-full ${maxHeight} overflow-auto rounded-md border p-m`}>
        {selectedValues.length < 1 ? (
          <div className="flex h-full w-full items-center justify-center bg-grey-100 text-center">
            <h5>Your selection</h5>
          </div>
        ) : (
          <SelectedItems />
        )}
      </div>
    </div>
  )
}

function SelectedItem({ item }) {
  const [detailsOpen, setDetailsOpen] = useState(false)

  const description = item.description || item.name

  return (
    <div key={`selection-${item.term_id}`} className="border p-xs">
      <h5 className="mb-0">{item.name}</h5>

      <SlideToggle open={detailsOpen}>
        <div
          className={`style-lists p--small py-xs [&_ul]:my-s`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </SlideToggle>

      <button className="p--xsmall text-primary" onClick={() => setDetailsOpen(!detailsOpen)}>
        {detailsOpen ? 'Hide details' : 'Show details'}
      </button>
    </div>
  )
}
